/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { IconEdit } from '@tabler/icons-react';
import { toast } from 'react-hot-toast';

import formatDate from '../../../../utilities/helper';
import useDebounce from '../../../../utilities/useDebounce';
import NewCommonModal from '../../../../components/common/NewCommonModal';
import ListView from '../../../../components/common/ListView';
import TableSkeleton from '../../../../components/skeleton/TableSkeleton';
import Pagination from '../../../../components/common/Pagination';
import NormalSearch from '../../../../components/common/NormalSearch';

import { useLocationSchemas } from './locationSchemas';
import { useDistrictList, useCreateDistrict, useUpdateDistrict, useAllCountries, useAllCities } from './locationHooks';
import DynamicForm from './DynamicForm';

export default function DistrictManageTab({ tab }: any) {
  const [searchFor, setSearchFor] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(searchFor, 500);

  const { data: countriesList } = useAllCountries();
  const { data: citiesList } = useAllCities(tab.data); // tab.data contains the country name

  const { data: distData, isLoading } = useDistrictList({
    countryId: tab.countryId,
    cityId: tab.params,
    page,
    searchFor: debouncedSearch,
  });
  const createMutation = useCreateDistrict();
  const updateMutation = useUpdateDistrict();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    clearErrors,
    formState: { errors },
  } = useForm<any>({ mode: 'onTouched' });
  const { schemas } = useLocationSchemas(countriesList, citiesList);
  const districtSchema = {
    ...schemas.districtAdd,
    fields: schemas.districtAdd?.fields?.map((field: any) => {
      if (isEdit && ['districtName', 'districtArabic'].includes(field.name)) {
        return { ...field, disabled: true };
      }
      return field;
    }) || [],
  };

  const handleFormSubmit = (data: any) => {
    const payload = { ...data, cityId: tab.params, countryId: tab.countryId };
    const mutation = isEdit ? updateMutation : createMutation;

    mutation.mutate(isEdit ? { id: selectedItem.id, payload } : payload, {
      onSuccess: () => {
        toast.success(`District ${isEdit ? 'updated' : 'added'} successfully`);
        closeAndReset();
      },
      onError: (err: any) => toast.error(err?.message || 'Operation failed'),
    });
  };

  const closeAndReset = () => {
    reset();
    setIsModalOpen(false);
    setIsEdit(false);
    setSelectedItem(null);
  };

  const columns = [
    {
      label: '#',
      render: (_: any, index: number) => (page - 1) * 10 + (index + 1),
    },
    { label: 'District Name', render: (s: any) => s.name },
    { label: 'Arabic Name', render: (s: any) => s.arabicName },
    { label: 'Alias English', render: (s: any) => s.aliasEnglish?.join(', ') },
    { label: 'Alias Arabic', render: (s: any) => s.aliasArabic?.join(', ') },
    { label: 'City', render: () => tab.cityName || '' },
    { label: 'Created At', render: (s: any) => formatDate(s.date) },
    {
      label: 'Action',
      render: (s: any) => (
        <div className="flex justify-center">
          <button
            onClick={() => {
              setIsEdit(true);
              setSelectedItem(s);
              setValue('countryId', tab.countryId);
              setValue('cityId', tab.params);
              setValue('districtName', s.name);
              setValue('districtArabic', s.arabicName);
              setValue('aliasEnglish', s.aliasEnglish || []);
              setValue('aliasArabic', s.aliasArabic || []);
              setIsModalOpen(true);
            }}
            className="p-2 border rounded-full bg-[#F7F6F2] hover:bg-gray-200 transition"
          >
            <IconEdit size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-6 mt-2">
        <div className="bg-white rounded-2xl border border-[#d2d2d2] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div>
              <h2 className="text-base font-semibold">Districts in {tab.cityName}</h2>
              <p className="text-xs text-gray-500">{tab.data}</p>
            </div>
            <div className="flex items-center gap-3">
              <NormalSearch
                value={searchFor}
                onChange={(e: any) => {
                  setSearchFor(e.target.value);
                  setPage(1);
                }}
              />
              {/* <button
                onClick={() => {
                  setValue('countryId', tab.countryId);
                  setValue('cityId', tab.params);
                  setIsEdit(false);
                  setIsModalOpen(true);
                }}
                className="px-4 py-2 bg-[#FBD037] hover:bg-yellow-500 text-black font-semibold rounded-full transition-all text-sm flex items-center justify-center"
              >
                Add District
              </button> */}
            </div>
          </div>
          {isLoading ? (
            <TableSkeleton
              columns={[
                '#',
                'District Name',
                'Arabic Name',
                'Alias English',
                'Alias Arabic',
                'City',
                'Created At',
                'Action',
              ]}
              rows={10}
            />
          ) : distData?.data?.districts?.length > 0 ? (
            <ListView columns={columns} data={distData.data.districts} />
          ) : (
            <div className="p-8 text-center text-gray-500">No Data Found</div>
          )}
        </div>
        {distData?.data?.totalPages > 1 && (
          <Pagination
            totalPages={distData.data.totalPages}
            fetchData={setPage}
            currentPage={page}
          />
        )}
      </div>

      <NewCommonModal
        title={isEdit ? 'Update District' : 'Add New District'}
        open={isModalOpen}
        onClose={closeAndReset}
        actionButtonText={isEdit ? 'Update' : 'Save'}
        actionButtonOnClick={handleSubmit(handleFormSubmit)}
        closeButtonText="Cancel"
      >
        <div className="bg-white p-6 rounded-b-[30px]">
          <DynamicForm
            schema={districtSchema}
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            control={control}
            clearErrors={clearErrors}
            className="grid-cols-1 md:grid-cols-6"
          />
        </div>
      </NewCommonModal>
    </>
  );
}
